import { useCallback, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";

// Mirror the whole-form buildPayload() per-field normalisation so an inline PATCH
// stores values identically to a full submit.
const NULL_IF_EMPTY = ["test_result", "blown_out_employee_id", "blown_out_date"];

export function normalizeField(key, value) {
  if (key === "vessel_number")
    return value === "" || value === null || value === undefined ? null : Number(value);
  if (key === "witness_tested_by_nobo") return !!value;
  if (NULL_IF_EMPTY.includes(key)) return value === "" ? null : value;
  return value;
}

/**
 * Field-level debounced inline save for an existing test record.
 * - scheduleSave(key, rawValue): debounces 400ms, then PATCH /tests/{id} with just that field.
 * - status[key]: "saving" | "saved" | "error" (auto-clears "saved" after 2s).
 * - errors[key]: inline error message on failure.
 * - On failure the field is reverted to the last server-confirmed value via onRevert.
 */
export function useInlineSave({ recordId, enabled, committedRef, onRevert }) {
  const [status, setStatus] = useState({});
  const [errors, setErrors] = useState({});
  const timers = useRef({});
  const savedClear = useRef({});

  const setKeyStatus = useCallback(
    (key, val) => setStatus((s) => ({ ...s, [key]: val })),
    [],
  );

  const clearKey = useCallback(
    (key) =>
      setStatus((s) => {
        if (s[key] !== "saved") return s;
        const n = { ...s };
        delete n[key];
        return n;
      }),
    [],
  );

  const doSave = useCallback(
    async (key, rawValue) => {
      setKeyStatus(key, "saving");
      setErrors((e) => {
        if (!(key in e)) return e;
        const n = { ...e };
        delete n[key];
        return n;
      });
      try {
        await api.patch(`/tests/${recordId}`, { [key]: normalizeField(key, rawValue) });
        committedRef.current[key] = rawValue;
        setKeyStatus(key, "saved");
        clearTimeout(savedClear.current[key]);
        savedClear.current[key] = setTimeout(() => clearKey(key), 2000);
      } catch (err) {
        setKeyStatus(key, "error");
        setErrors((e) => ({ ...e, [key]: formatApiError(err) }));
        onRevert(key, committedRef.current[key]);
      }
    },
    [recordId, committedRef, onRevert, setKeyStatus, clearKey],
  );

  const scheduleSave = useCallback(
    (key, rawValue) => {
      if (!enabled || !recordId) return;
      // No-op when the value already matches the last server-confirmed value.
      // Prevents redundant PATCHes and defends against transient re-render churn.
      if (rawValue === committedRef.current[key]) return;
      clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => doSave(key, rawValue), 400);
    },
    [enabled, recordId, doSave, committedRef],
  );

  return { status, errors, scheduleSave };
}
